/**
 * WooCommerce Table Responsive Wrapper
 *
 * WooCommerce generates tables directly into the DOM without a scroll wrapper.
 * This utility wraps targeted WC tables in a .wc-table-scroll-wrapper element
 * so they receive the same horizontal-scroll treatment (border, radius,
 * scroll-shadow, keyboard-accessible overflow) as the theme's PHP table component.
 *
 * WCAG 2.1 SC 1.4.10 (Reflow) + SC 2.1.1 (Keyboard): the wrapper carries
 * role="region", aria-label, and tabindex="0" so keyboard users can scroll.
 *
 * Targeted selectors (all scoped to .woocommerce / .woocommerce-page):
 *   table.shop_table              — cart, checkout review, order details, order tracking
 *   table.woocommerce-orders-table — my account orders list
 *
 * @package THS_Theme
 * @since 2.1.0
 */

/**
 * WC table selectors to wrap.
 * Scope to WC containers to avoid matching unrelated tables.
 */
const WC_TABLE_SELECTORS = [
  '.woocommerce table.shop_table',
  '.woocommerce-page table.shop_table',
  '.woocommerce table.woocommerce-orders-table',
  '.woocommerce-page table.woocommerce-orders-table'
]

/**
 * Derive an accessible label from the table's <caption> text or a data
 * attribute on the table, falling back to a generic label.
 *
 * @param {HTMLTableElement} table
 * @returns {string}
 */
function getTableLabel (table) {
  const caption = table.querySelector('caption')
  if (caption && caption.textContent.trim()) {
    return caption.textContent.trim()
  }

  const ariaLabel = table.getAttribute('aria-label')
  if (ariaLabel) {
    return ariaLabel
  }

  // Derive from WC class names as a readable fallback
  const classMap = {
    order_details: 'Order details',
    cart: 'Cart',
    'woocommerce-checkout-review-order-table': 'Order summary',
    my_account_orders: 'Orders',
    shop_table: 'Table'
  }

  for (const [cls, label] of Object.entries(classMap)) {
    if (table.classList.contains(cls)) {
      return label
    }
  }

  return 'Table'
}

/**
 * Wrap a single table in a .wc-table-scroll-wrapper div.
 * No-ops if the table is already wrapped.
 *
 * @param {HTMLTableElement} table
 */
function wrapTable (table) {
  // Already wrapped — skip
  if (table.parentElement && table.parentElement.classList.contains('wc-table-scroll-wrapper')) {
    return
  }

  const wrapper = document.createElement('div')
  wrapper.className = 'wc-table-scroll-wrapper'
  wrapper.setAttribute('role', 'region')
  wrapper.setAttribute('aria-label', getTableLabel(table))
  wrapper.setAttribute('tabindex', '0')

  table.parentNode.insertBefore(wrapper, table)
  wrapper.appendChild(table)
}

/**
 * Initialise WC table wrapping.
 * Queries all matching tables and wraps any that are not already wrapped.
 */
export function initWcTables () {
  const selector = WC_TABLE_SELECTORS.join(', ')
  const tables = document.querySelectorAll(selector)

  tables.forEach(wrapTable)
}

/**
 * Re-wrap tables after WooCommerce cart AJAX updates.
 *
 * WooCommerce fires these jQuery custom events on document.body after any
 * cart fragment refresh, meaning the DOM has been replaced and our wrappers
 * are gone. jQuery is always available in WordPress so we bind directly.
 *
 * Events:
 *   updated_cart_totals  — fired after the cart totals block is updated
 *   wc_fragments_refreshed — fired after all cart fragments (incl. cart table) are refreshed
 *   updated_checkout     — fired after the checkout order review table is updated
 */
let wcTablesObserverInitialised = false

export function initWcTablesCartObserver () {
  if (typeof window.jQuery === 'undefined') {
    return
  }

  if (wcTablesObserverInitialised) {
    return
  }
  wcTablesObserverInitialised = true

  window.jQuery(document.body).on(
    'updated_cart_totals wc_fragments_refreshed updated_checkout',
    initWcTables
  )
}
